import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import FailedWoDetailsModal from "./FailedWoDetailsModal";
import { getAuthHeaders } from "../Auth/authHelpers";
import "./WorkOrderDetailsPage.css";

// ─── Progress helper ─────────────────────────────────────────────────────────
function computeProgress(passedWOs, requirements) {
	if (!requirements) return {};

	const aliasMap = {
		weekly: "1w",
		biweekly: "2w",
		monthly: "4w",
		quarterly: "13w",
		semiannual: "26w",
		semiannually: "26w",
		annual: "52w",
		annually: "52w",
		"1w": "1w",
		"2w": "2w",
		"4w": "4w",
		"13w": "13w",
		"26w": "26w",
		"52w": "52w",
	};

	const normalize = (text) =>
		aliasMap[text?.toLowerCase()] || text?.toLowerCase();

	const wordBag = (s) =>
		(s || "")
			.toLowerCase()
			.split(/[^a-z0-9]+/)
			.map((w) => normalize(w))
			.filter(Boolean);

	const coreWords = (arr) =>
		arr.filter((w) => !["min", "max", "minimum", "maximum"].includes(w));

	const matchesRule = (rule, type) => {
		const ruleWords = coreWords(wordBag(rule));
		const typeWords = wordBag(type || "");
		return ruleWords.every((rw) => typeWords.some((tw) => tw.startsWith(rw)));
	};

	const ruleCounts = {};
	const used = new Set();
	const blocked = new Set();
	Object.keys(requirements).forEach((r) => (ruleCounts[r] = 0));

	const dailyKey = Object.keys(requirements).find(
		(k) => k.toLowerCase().includes("daily") && k.toLowerCase().includes("max"),
	);

	if (dailyKey) {
		const cap = requirements[dailyKey];
		const dailyList = passedWOs.filter((wo) => matchesRule(dailyKey, wo.Type));

		dailyList.forEach((wo, idx) => {
			const id = wo.wo_num;
			if (idx < cap) {
				ruleCounts[dailyKey] += 1;
			} else {
				blocked.add(id);
				used.add(id);
			}
		});
	}

	Object.keys(requirements).forEach((key) => {
		if (!key.toLowerCase().includes("min")) return;
		const need = requirements[key];
		let taken = 0;

		passedWOs.forEach((wo) => {
			if (taken < need && matchesRule(key, wo.Type)) {
				ruleCounts[key] += 1;
				taken += 1;
			}
		});
	});

	Object.keys(requirements).forEach((key) => {
		if (!key.toLowerCase().includes("max") || key === dailyKey) return;
		const cap = requirements[key];
		let taken = 0;

		passedWOs.forEach((wo) => {
			const id = wo.wo_num;
			if (taken < cap && !used.has(id) && matchesRule(key, wo.Type)) {
				ruleCounts[key] += 1;
				taken += 1;
				used.add(id);
			}
		});
	});

	const flexKey = Object.keys(requirements).find((k) =>
		k.toLowerCase().includes("flexible"),
	);

	if (flexKey) {
		const cap = requirements[flexKey];
		let taken = 0;

		passedWOs.forEach((wo) => {
			const id = wo.wo_num;
			if (taken < cap && !used.has(id) && !blocked.has(id)) {
				ruleCounts[flexKey] += 1;
				taken += 1;
				used.add(id);
			}
		});
	}

	return ruleCounts;
}

function ruleMet(ruleKey, count, requirement) {
	const k = ruleKey.toLowerCase();

	if (ruleKey === "total_required") return count >= requirement;
	if (k.includes("max")) return count <= requirement;
	if (k.includes("min")) return count >= requirement;
	return count >= requirement;
}

const calculateAttemptsAndDetails = (data) => {
	const sortedData = [...data].sort((a, b) => {
		const attemptA = parseInt(
			String(a.wd_trans_pk || "")
				.split("-")
				.pop(),
			10,
		);
		const attemptB = parseInt(
			String(b.wd_trans_pk || "")
				.split("-")
				.pop(),
			10,
		);
		return attemptA - attemptB;
	});

	let totalAttempts = 0;
	let attemptsToPass = 0;
	let lockoutStatus = "N/A";
	let foundPass = false;
	let compStatus = "N/A";

	for (const attempt of sortedData) {
		const grade = attempt.grade?.trim();
		const compSts = attempt.comp_sts?.trim();
		const attemptNumber = parseInt(
			String(attempt.wd_trans_pk || "")
				.split("-")
				.pop(),
			10,
		);

		totalAttempts++;

		if (grade === "Pass") {
			foundPass = true;
			compStatus = "Completed";
			lockoutStatus = "No";
			if (attemptsToPass === 0) attemptsToPass = totalAttempts;
			break;
		}

		if (grade === "Fail" && !foundPass) {
			compStatus =
				compSts === "In Progress" ? "In Progress - Fail" : "In Progress";

			if (attemptNumber === 1) lockoutStatus = "No";
			else if (attemptNumber === 2) lockoutStatus = "Yes";
			else if (attemptNumber > 2) lockoutStatus = "Unlocked";
		}
	}

	return { totalAttempts, attemptsToPass, lockoutStatus, compStatus };
};

const WorkOrderDetailsPage = ({ trainerID }) => {
	const { alias, coreCompCode } = useParams();
	const { state } = useLocation();
	const navigate = useNavigate();
	const API = process.env.REACT_APP_API_URL;

	const formattedAlias = String(alias || "")
		.trim()
		.toLowerCase();
	const formattedCoreCompCode = String(coreCompCode || "")
		.trim()
		.toLowerCase();

	const authHeaders = useCallback(
		() => getAuthHeaders(trainerID),
		[trainerID],
	);

	const maxAppeals = 2;

	const defaultState = {
		courseTitle: "N/A",
		courseCode: "N/A",
		courseStatus: "N/A",
		courseType: "N/A",
		courseCredits: "N/A",
		courseReqWos: "N/A",
		courseStartDate: "N/A",
		courseCompletionDate: "N/A",
		creditWoPassed: 0,
	};

	const currentState = state || defaultState;

	const [loading, setLoading] = useState(true);
	const [workOrders, setWorkOrders] = useState([]);
	const [progressData, setProgressData] = useState(null);
	const [workdayDetails, setWorkdayDetails] = useState({
		compStatus: "N/A",
		compDate: "N/A",
		attempts: 0,
		lockoutStatus: "N/A",
		attemptsToPass: "N/A",
	});
	const [etpDates, setEtpDates] = useState({
		wdStatus: "N/A",
		wdStartDate: "N/A",
		wdCompletedDate: "N/A",
	});
	const [appealsCount, setAppealsCount] = useState(0);

	const [failedWoDetails, setFailedWoDetails] = useState([]);
	const [showFailedWoModal, setShowFailedWoModal] = useState(false);

	useEffect(() => {
		if (!alias || !coreCompCode || !trainerID) return;

		const run = async () => {
			setLoading(true);
			try {
				const [woRes, progRes, wdRes, appealRes, etpRes] = await Promise.all([
					fetch(
						`${API}/failed-wo/work-orders/${encodeURIComponent(formattedAlias)}/${encodeURIComponent(formattedCoreCompCode)}`,
						{ headers: authHeaders },
					),
					fetch(
						`${API}/failed-wo/core-progress/${encodeURIComponent(formattedAlias)}/${encodeURIComponent(formattedCoreCompCode)}`,
						{ headers: authHeaders },
					),
					fetch(
						`${API}/workday-courses/${encodeURIComponent(formattedAlias)}/${encodeURIComponent(formattedCoreCompCode)}`,
						{ headers: authHeaders },
					),
					fetch(
						`${API}/failed-wo/appeals-count/${encodeURIComponent(formattedAlias)}/${encodeURIComponent(formattedCoreCompCode)}`,
						{ headers: authHeaders },
					),
					fetch(`${API}/etp-mstr/alias/${encodeURIComponent(formattedAlias)}`, {
						headers: await authHeaders(),
					}),
				]);

				if (woRes.ok) {
					const woJson = await woRes.json();
					setWorkOrders(Array.isArray(woJson) ? woJson : []);
				} else {
					console.error("WO details failed:", woRes.status);
					setWorkOrders([]);
				}

				if (progRes.ok) {
					const progJson = await progRes.json();
					setProgressData(progJson || null);
				} else {
					console.error("Core progress failed:", progRes.status);
					setProgressData(null);
				}

				if (wdRes.ok) {
					const wdJson = await wdRes.json();
					const rows = Array.isArray(wdJson) ? wdJson : [];

					if (rows.length > 0) {
						const trimmed = rows.map((r) => ({
							...r,
							wd_trans_pk: String(r.wd_trans_pk || "").trim(),
							core_comp_code: String(r.core_comp_code || "").trim(),
						}));

						const { totalAttempts, attemptsToPass, lockoutStatus, compStatus } =
							calculateAttemptsAndDetails(trimmed);

						const latestCompletedRow =
							trimmed.find((r) => String(r.grade || "").trim() === "Pass") ||
							trimmed[0];

						setWorkdayDetails({
							compStatus,
							compDate:
								latestCompletedRow?.wd_date_completed ||
								latestCompletedRow?.comp_date
								? String(
										latestCompletedRow.wd_date_completed ||
											latestCompletedRow.comp_date,
									).split("T")[0]
								: "N/A",
							attempts: totalAttempts,
							lockoutStatus,
							attemptsToPass: attemptsToPass || "N/A",
						});
					} else {
						setWorkdayDetails({
							compStatus: "N/A",
							compDate: "N/A",
							attempts: 0,
							lockoutStatus: "N/A",
							attemptsToPass: "N/A",
						});
					}
				} else {
					console.error("Workday course fetch failed:", wdRes.status);
				}

				if (etpRes.ok) {
					const etpJson = await etpRes.json();
					const rows = Array.isArray(etpJson) ? etpJson : [];
					const match = rows.find(
						(r) =>
							String(r?.etp_mstr_core_comp_code || "")
								.trim()
								.toLowerCase() === formattedCoreCompCode,
					);

					setEtpDates({
						wdStatus: match?.etp_mstr_wd_status || "N/A",
						wdStartDate: match?.wd_start_date
							? String(match.wd_start_date).split("T")[0]
							: "N/A",
						wdCompletedDate: match?.wd_date_completed
							? String(match.wd_date_completed).split("T")[0]
							: "N/A",
					});
				} else {
					console.error("ETP alias fetch failed:", etpRes.status);
					setEtpDates({
						wdStatus: "N/A",
						wdStartDate: "N/A",
						wdCompletedDate: "N/A",
					});
				}

				if (appealRes.ok) {
					const a = await appealRes.json();
					setAppealsCount(Number(a?.appealCount || 0));
				} else {
					console.error("Appeals count failed:", appealRes.status);
					setAppealsCount(0);
				}
			} catch (e) {
				console.error("WorkOrderDetailsPage fetch error:", e);
				setWorkOrders([]);
				setProgressData(null);
			} finally {
				setLoading(false);
			}
		};

		run();
	}, [
		API,
		alias,
		coreCompCode,
		formattedAlias,
		formattedCoreCompCode,
		trainerID,
		authHeaders,
	]);

	const passed = useMemo(
		() =>
			(workOrders || []).filter(
				(wo) => String(wo.final_status || "").toLowerCase() === "pass",
			),
		[workOrders],
	);

	const failed = useMemo(
		() =>
			(workOrders || []).filter(
				(wo) => String(wo.final_status || "").toLowerCase() === "fail",
			),
		[workOrders],
	);

	const passedCount = passed.length;
	const failedCount = failed.length;
	const totalCount = workOrders.length;

	const progressView = useMemo(() => {
		const reqs = progressData?.requirements || {};
		const counts = computeProgress(passed, reqs);

		const totalRequiredRaw =
			progressData?.total_required ?? currentState.courseReqWos;
		const totalRequired = Number(totalRequiredRaw || 0);

		const effectiveReq = { ...reqs };
		if (totalRequired > 0) {
			effectiveReq.total_required = totalRequired;
			counts.total_required = passedCount;
		}

		const allMet = Object.keys(effectiveReq).every((rk) =>
			ruleMet(rk, Number(counts[rk] || 0), Number(effectiveReq[rk] || 0)),
		);

		const flexKey = Object.keys(effectiveReq).find((k) =>
			k.toLowerCase().includes("flexible"),
		);
		const keys = Object.keys(effectiveReq).filter((k) => k !== flexKey);

		const items = keys.map((ruleKey) => {
			const req = Number(effectiveReq[ruleKey] || 0);
			const count = Number(counts[ruleKey] || 0);
			const met = ruleMet(ruleKey, count, req);

			let label = ruleKey
				.replace(/_/g, " ")
				.replace(/min$/i, "Minimum")
				.replace(/max$/i, "Maximum");
			if (ruleKey === "total_required") label = "Total Work Orders";

			return { ruleKey, label, req, count, met };
		});

		const flex = flexKey
			? {
					ruleKey: flexKey,
					req: Number(effectiveReq[flexKey] || 0),
					count: Number(counts[flexKey] || 0),
				}
			: null;

		return { allMet, items, flex };
	}, [progressData, passed, passedCount, currentState.courseReqWos]);

	const displayedCourseStatus = useMemo(() => {
		if (
			String(currentState.courseStatus || "").toLowerCase() === "completed" ||
			(String(currentState.courseCompletionDate || "").trim() !== "" &&
				String(currentState.courseCompletionDate || "").trim() !== "N/A")
		) {
			return "Completed";
		}

		if (workdayDetails.compStatus === "Completed" && progressView.allMet) {
			return "Ready / Completed by requirements";
		}

		return currentState.courseStatus || "N/A";
	}, [currentState, workdayDetails, progressView]);

	const handleOpenFailedWoModal = async () => {
		try {
			const res = await fetch(
				`${API}/failed-wo/all-wo-details/${encodeURIComponent(alias)}/${encodeURIComponent(coreCompCode)}`,
				{
					headers: await authHeaders(),
				},
			);
			if (!res.ok) throw new Error("Unable to fetch all work order details.");
			const allDetails = await res.json();
			setFailedWoDetails(Array.isArray(allDetails) ? allDetails : []);
			setShowFailedWoModal(true);
		} catch (e) {
			console.error(e);
			alert("Unable to fetch all work order details.");
		}
	};

	if (loading) {
		return (
			<div className="wod-page">
				<div className="wod-loading">Loading…</div>
			</div>
		);
	}

	return (
		<div className="wod-page">
			<div className="wod-shell">
				<div className="wod-topbar">
					<button
						className="wod-btn wod-btn-light"
						onClick={() => navigate(-1)}
					>
						&larr; Back
					</button>

					<div className="wod-title">
						<div className="wod-title-main">{currentState.courseTitle}</div>
						<div className="wod-title-sub">
							{currentState.courseCode} • {coreCompCode?.toUpperCase()}
						</div>
					</div>

					<button
						className="wod-btn wod-btn-dark"
						onClick={handleOpenFailedWoModal}
					>
						View All Work Orders
					</button>
				</div>

				<div className="wod-cards">
					<div className="wod-card">
						<div className="wod-card-title">Course</div>
						<div className="wod-row">
							<span className="k">Type</span>
							<span className="v">{currentState.courseType}</span>
						</div>
						<div className="wod-row">
							<span className="k">Status</span>
							<span className="v">{displayedCourseStatus}</span>
						</div>
						<div className="wod-row">
							<span className="k">Start</span>
							<span className="v">{currentState.courseStartDate}</span>
						</div>
						<div className="wod-row">
							<span className="k">Completion</span>
							<span className="v">{currentState.courseCompletionDate}</span>
						</div>
					</div>

					<div className="wod-card">
						<div className="wod-card-title">Work Orders</div>
						<div className="wod-row">
							<span className="k">Required</span>
							<span className="v">{currentState.courseReqWos}</span>
						</div>
						<div className="wod-row">
							<span className="k">Total</span>
							<span className="v">{totalCount}</span>
						</div>
						<div className="wod-row">
							<span className="k">Passed</span>
							<span className="v">{passedCount}</span>
						</div>
						<div className="wod-row">
							<span className="k">Failed</span>
							<span className="v">{failedCount}</span>
						</div>
						<div className="wod-row">
							<span className="k">Appeals</span>
							<span className="v">
								{appealsCount} / {maxAppeals}
							</span>
						</div>
					</div>

					<div className="wod-card">
						<div className="wod-card-title">Workday</div>
						<div className="wod-row">
							<span className="k">Status</span>
							<span className="v">{etpDates.wdStatus}</span>
						</div>
						<div className="wod-row">
							<span className="k">Started</span>
							<span className="v">{etpDates.wdStartDate}</span>
						</div>
						<div className="wod-row">
							<span className="k">Completed</span>
							<span className="v">
								{etpDates.wdCompletedDate !== "N/A"
									? etpDates.wdCompletedDate
									: workdayDetails.compDate}
							</span>
						</div>
						<div className="wod-row">
							<span className="k">Attempts</span>
							<span className="v">{workdayDetails.attempts}</span>
						</div>
						<div className="wod-row">
							<span className="k">Lockout</span>
							<span className="v">{workdayDetails.lockoutStatus}</span>
						</div>
					</div>
				</div>

				<div className="wod-progress">
					<div className="wod-progress-head">
						<div>
							<div className="wod-progress-title">Progress</div>
							<div className="wod-progress-sub">
								Rules for {coreCompCode?.toUpperCase()}
							</div>
						</div>

						<span className={`wod-pill ${progressView.allMet ? "ok" : "bad"}`}>
							{progressView.allMet ? "Overall Complete" : "Overall Incomplete"}
						</span>
					</div>

					{!progressData?.requirements ? (
						<div className="wod-muted">No requirements available.</div>
					) : (
						<>
							<div className="wod-rule-grid">
								{progressView.items.map((it) => (
									<div
										key={it.ruleKey}
										className={`wod-rule ${it.met ? "met" : "notmet"}`}
									>
										<div className="wod-rule-left">
											<div className={`wod-icon ${it.met ? "ok" : "bad"}`}>
												{it.met ? "✓" : "!"}
											</div>
											<div>
												<div className="wod-rule-name">{it.label}</div>
												<div className="wod-rule-sub">
													{it.count} of {it.req}
												</div>
											</div>
										</div>

										<span className={`wod-pill ${it.met ? "ok" : "bad"}`}>
											{it.met ? "Met" : "Not met"}
										</span>
									</div>
								))}
							</div>

							{progressView.flex && (
								<div className="wod-flex">
									<div>
										<strong>Flexible:</strong> {progressView.flex.count} of{" "}
										{progressView.flex.req} filled
									</div>
									<div className="wod-muted">
										Uses overlap from other templates — does not increase total
										count.
									</div>
								</div>
							)}

							{!progressView.allMet && (
								<div className="wod-hint">
									Even if the total is satisfied, you must also meet each
									category minimum/maximum for overall completion.
								</div>
							)}
						</>
					)}
				</div>

				<div className="wod-table-shell">
					<div className="wod-table-title">All Submitted Work Orders</div>

					<table className="wod-table">
						<thead>
							<tr>
								<th>WO #</th>
								<th>Completion</th>
								<th>Site</th>
								<th>Status</th>
								<th>Type</th>
								<th>Template</th>
								<th>Reason</th>
							</tr>
						</thead>
						<tbody>
							{workOrders.length > 0 ? (
								workOrders.map((wo) => {
									const status = String(wo.final_status || "N/A").toLowerCase();
									const dup = String(wo.duplicate_wo || "").toLowerCase();

									let reason = wo.final_status || "N/A";
									if (dup === "ai") reason = "Use of AI detected";
									else if (dup === "true") reason = "Duplicate";

									return (
										<tr key={wo.bert_wo_dtls_id || wo.wo_num}>
											<td className="mono">{wo.wo_num}</td>
											<td>{wo.wo_comp_date?.split("T")[0] || ""}</td>
											<td>{wo.site_id}</td>
											<td>
												<span className={`wod-status ${status}`}>
													{status === "pass"
														? "Pass"
														: status === "fail"
															? "Fail"
															: wo.final_status}
												</span>
											</td>
											<td>{wo.Type || "—"}</td>
											<td>{wo.template}</td>
											<td>{reason}</td>
										</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={7} className="wod-empty">
										No work orders available
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{showFailedWoModal && (
					<FailedWoDetailsModal
						details={failedWoDetails}
						onClose={() => setShowFailedWoModal(false)}
						alias={alias}
						coreCompCode={coreCompCode}
						appealsCount={appealsCount}
						maxAppeals={maxAppeals}
					/>
				)}
			</div>
		</div>
	);
};

export { computeProgress, ruleMet };
export default WorkOrderDetailsPage;
